import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

/**
 * `instrumented` wraps a React Component with event handlers that log
 *  traffic events. `standardEvents` are a list of event handlers
 *  already defined on the event, for example, `onClick` will log the
 *  `click` event to traffic. If you want to log a different event name,
 *  provide an event handler with a function that returns the event type
 *  and the event detail you want in `customEvents` (see
 *  InstrumentedCheckbox for an example).
 */
export function instrumented(WrappedComponent, standardEvents, customEvents, options = {}) {
  class Instrumented extends React.Component {
    constructor(props) {
      super(props);

      this.events = {};
      const stdEvents = standardEvents || [];
      stdEvents.forEach(eventName => {
        this.events[eventName] = (...args) => {
          const shortEventName = eventName.startsWith('on') ? eventName.substring(2).toLowerCase() : eventName;
          this.logTraffic(shortEventName, shortEventName);
          if (this.props.passThroughProps[eventName]) {
            return this.props.passThroughProps[eventName](...args);
          }
          return true;
        };
      });

      const custEvents = customEvents || {};
      Object.entries(custEvents).forEach(([eventName, func]) => {
        this.events[eventName] = (...args) => {
          this.logTraffic(...func(event, this.props.passThroughProps));
          if (this.props.passThroughProps[eventName]) {
            return this.props.passThroughProps[eventName](...args);
          }
          return true;
        };
      });

      // init custom event onUserChange that need to work in the way like native html onChange is
      this.value = undefined;
      this.extraEvents = {};
      this.initOnUserChange(this.events);
    }

    componentDidMount() {
      if ('onLoad' in this.events) {
        this.logTraffic('load', 'load');
      }
    }

    logTraffic(eventType, eventDetail) {
      if (eventDetail === undefined || this.props.traffic == null) {
        return;
      }
      const detail = eventDetail || eventType;
      const { trafficName } = this.props;
      this.props.traffic(eventType, `${trafficName}.${detail}`);
    }

    initOnUserChange = () => {
      const eventTypes = Object.keys(this.events);

      this.extraEvents =
        eventTypes.indexOf('onUserChange') === -1
          ? {}
          : {
              onBlur: this.onBlur,
              onFocus: this.onFocus,
              onKeyPress: this.onKeyPress,
            };
    };

    onKeyPress = event => {
      if (event.key === 'Enter') {
        this.completeChange(event);
      }

      this.processFallbackEvent(event, 'onKeyPress');
    };

    onBlur = event => {
      this.completeChange(event);

      this.processFallbackEvent(event, 'onBlur');
    };

    onFocus = event => {
      this.value = event.target.value;

      this.processFallbackEvent(event, 'onFocus');
    };

    processFallbackEvent = (event, eventName) => {
      if (this.events[eventName]) {
        return this.events[eventName](event);
      } else if (this.props.passThroughProps[eventName]) {
        return this.props.passThroughProps[eventName](event);
      }
    };

    completeChange = event => {
      if (event.target.value !== this.value) {
        this.value = event.target.value;
        this.events.onUserChange && this.events.onUserChange(event.target.value);
      }
    };

    render() {
      const sharedProps = {};
      // One challenge with wrapping components is figuring out which
      // props are only meant for the wrapper, and which belong to the
      // wrapped component. In our case, dispatch is injected b/c this is
      // a connected component. If we're wrapping a connected component,
      // it will get its own dispatch, and we don't need to pass it
      // along. The `traffic` prop could be meant for either; since we're
      // setting that up in mapStateToProps rather than receiving it, we
      // can safely keep that just within the wrapper; if the component
      // gets `traffic` passed to it, we'll leave that alone. Finally,
      // `trafficName` is ambiguous; the wrapper definitely needs it,
      // but the wrapped component might also. We'll check the propTypes
      // of the wrapped component to decide whether to pass it along.
      if (!options.doNotPassTrafficName) {
        sharedProps.trafficName = this.props.trafficName;
      }

      const props = { ...this.props.passThroughProps, ...sharedProps, ...this.events, ...this.extraEvents };

      // remove onUserChange from properties passed to component, this property relates to wrapper itself
      if (props.onUserChange) {
        delete props.onUserChange;
      }

      return <WrappedComponent {...props} />;
    }
  }

  Instrumented.propTypes = {
    dispatch: PropTypes.func.isRequired,
    traffic: PropTypes.func,
    trafficName: PropTypes.string,
    passThroughProps: PropTypes.object.isRequired,
  };

  const InstrumentedComponent = connect(mapStateToProps)(Instrumented);
  // Allow access to the original component, without the double wrapping
  // by Instrumented and Connected. This is useful for unit tests.
  InstrumentedComponent.Unwrapped = WrappedComponent;
  return InstrumentedComponent;
}

function mapStateToProps(state, ownProps) {
  const { dispatch, trafficName, ...passThroughProps } = ownProps;
  return {
    dispatch,
    trafficName,
    passThroughProps,
    traffic: state.globals.traffic,
  };
}
